import type { DeltaEWeights, Lab } from './types'

const DEG = Math.PI / 180
const POW25_7 = 25 ** 7

const rad = (deg: number) => deg * DEG

/** atan2 in degrees, wrapped into [0, 360). Returns 0 when both arguments are 0. */
function hueAngle(b: number, a: number): number {
  if (a === 0 && b === 0) return 0
  const deg = Math.atan2(b, a) / DEG
  return deg < 0 ? deg + 360 : deg
}

/**
 * CIEDE2000 colour difference between two L*a*b* samples.
 *
 * Follows the CIE formulation with the three discontinuities handled explicitly:
 * hue angles are wrapped into [0, 360), the hue difference takes the shorter arc
 * across the 180-degree boundary, and both hue terms collapse when either sample
 * is achromatic (C'1 * C'2 === 0), where the hue angle is undefined.
 */
export function deltaE2000(sample: Lab, reference: Lab, weights: DeltaEWeights = {}): number {
  const { kL = 1, kC = 1, kH = 1 } = weights

  const { L: L1, a: a1, b: b1 } = sample
  const { L: L2, a: a2, b: b2 } = reference

  const C1 = Math.hypot(a1, b1)
  const C2 = Math.hypot(a2, b2)
  const cBar = (C1 + C2) / 2

  // G expands the a* axis in the neutral region, where the formula is least uniform.
  const cBar7 = cBar ** 7
  const G = 0.5 * (1 - Math.sqrt(cBar7 / (cBar7 + POW25_7)))

  const a1p = (1 + G) * a1
  const a2p = (1 + G) * a2

  const C1p = Math.hypot(a1p, b1)
  const C2p = Math.hypot(a2p, b2)

  const h1p = hueAngle(b1, a1p)
  const h2p = hueAngle(b2, a2p)

  const dLp = L2 - L1
  const dCp = C2p - C1p

  const chromaProduct = C1p * C2p

  let dhp: number
  if (chromaProduct === 0) {
    dhp = 0
  } else {
    const diff = h2p - h1p
    if (diff > 180) dhp = diff - 360
    else if (diff < -180) dhp = diff + 360
    else dhp = diff
  }
  const dHp = 2 * Math.sqrt(chromaProduct) * Math.sin(rad(dhp) / 2)

  const lBarP = (L1 + L2) / 2
  const cBarP = (C1p + C2p) / 2

  let hBarP: number
  if (chromaProduct === 0) {
    hBarP = h1p + h2p
  } else if (Math.abs(h1p - h2p) <= 180) {
    hBarP = (h1p + h2p) / 2
  } else if (h1p + h2p < 360) {
    hBarP = (h1p + h2p + 360) / 2
  } else {
    hBarP = (h1p + h2p - 360) / 2
  }

  const T =
    1 -
    0.17 * Math.cos(rad(hBarP - 30)) +
    0.24 * Math.cos(rad(2 * hBarP)) +
    0.32 * Math.cos(rad(3 * hBarP + 6)) -
    0.2 * Math.cos(rad(4 * hBarP - 63))

  const dTheta = 30 * Math.exp(-(((hBarP - 275) / 25) ** 2))

  const cBarP7 = cBarP ** 7
  const Rc = 2 * Math.sqrt(cBarP7 / (cBarP7 + POW25_7))

  const lBarOffset = (lBarP - 50) ** 2
  const Sl = 1 + (0.015 * lBarOffset) / Math.sqrt(20 + lBarOffset)
  const Sc = 1 + 0.045 * cBarP
  const Sh = 1 + 0.015 * cBarP * T

  const Rt = -Math.sin(rad(2 * dTheta)) * Rc

  const termL = dLp / (kL * Sl)
  const termC = dCp / (kC * Sc)
  const termH = dHp / (kH * Sh)

  return Math.sqrt(termL ** 2 + termC ** 2 + termH ** 2 + Rt * termC * termH)
}

/** Plain Euclidean distance in L*a*b*, the older CIE76 difference. Kept for display only. */
export function deltaE76(sample: Lab, reference: Lab): number {
  return Math.hypot(sample.L - reference.L, sample.a - reference.a, sample.b - reference.b)
}
